// apps/web/src/components/forms/MfaCodeField.tsx
//
// Plan 04-12b Task 2 — MFA code input fragment.
//
// Accepts BOTH formats:
//   - 6-digit TOTP code (Better-Auth twoFactor plugin default)
//   - 8-char backup code formatted XXXX-XXXX where X is from Plan 04-09's
//     no-confusable-chars alphabet (ABCDEFGHJKMNPQRSTUVWXYZ23456789)
//
// The toggle exposed via `onToggle` lets the parent switch between the two
// modes. The Zod schema enforces the format per-mode.

import type * as React from "react";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import type { Control, FieldValues, Path } from "react-hook-form";

export type MfaCodeMode = "totp" | "backup";

export const MFA_CODE_PATTERNS = {
  totp: /^\d{6}$/,
  backup: /^[ABCDEFGHJKMNPQRSTUVWXYZ23456789]{4}-[ABCDEFGHJKMNPQRSTUVWXYZ23456789]{4}$/,
} as const;

export interface MfaCodeFieldProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  name: Path<TFieldValues>;
  mode: MfaCodeMode;
  onToggleMode?: (next: MfaCodeMode) => void;
  disabled?: boolean;
  /** When true, suppress the "Use backup code instead" toggle (used on the
   * enrollment confirm step where backup codes aren't available yet). */
  hideBackupToggle?: boolean;
}

export function MfaCodeField<TFieldValues extends FieldValues>(
  props: MfaCodeFieldProps<TFieldValues>,
): React.ReactElement {
  const { control, name, mode, onToggleMode, disabled, hideBackupToggle } = props;
  const isTotp = mode === "totp";
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>
            {isTotp ? "Authenticator code" : "Backup code"}
          </FormLabel>
          <FormControl>
            <Input
              type="text"
              inputMode={isTotp ? "numeric" : "text"}
              autoComplete="one-time-code"
              placeholder={isTotp ? "123456" : "XXXX-XXXX"}
              maxLength={isTotp ? 6 : 9}
              disabled={disabled}
              {...field}
            />
          </FormControl>
          <FormDescription>
            {isTotp
              ? "Enter the 6-digit code from your authenticator app."
              : "Enter one of your saved backup codes (format XXXX-XXXX)."}
          </FormDescription>
          {!hideBackupToggle && onToggleMode !== undefined ? (
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto p-0 text-xs"
              onClick={() => onToggleMode(isTotp ? "backup" : "totp")}
              disabled={disabled}
            >
              {isTotp ? "Use a backup code instead" : "Use your authenticator app instead"}
            </Button>
          ) : null}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
